import { useEffect, useRef, useState } from "react";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Upload, Search, BarChart3, Megaphone, Globe, Sparkles, Loader2, HelpCircle } from "lucide-react";

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-slate-100">{label}</Label>
    {hint ? <p className="text-xs text-slate-500 mt-0.5">{hint}</p> : null}
    <div className="mt-1.5">{children}</div>
  </div>
);

const inputCls = "bg-slate-800 border-slate-700 text-white";

export default function SuperSeo() {
  const [seo, setSeo] = useState(null);
  const [mkt, setMkt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [claudeOn, setClaudeOn] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/landing");
      setSeo({ robots_index: true, faq: [], ...(data.seo || {}) });
      setMkt(data.marketing || {});
    } catch (e) { toast.error(extractErr(e)); }
    api.get("/platform/blog/config").then(({ data }) => setClaudeOn(Boolean(data?.enabled))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/platform/landing", { seo, marketing: mkt });
      setSeo({ robots_index: true, faq: [], ...(data.seo || {}) });
      setMkt(data.marketing || {});
      toast.success("SEO & marketing settings saved");
    } catch (e) { toast.error(extractErr(e)); } finally { setSaving(false); }
  };

  const uploadOg = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/platform/landing/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSeo((s) => ({ ...s, og_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };

  const generateSeo = async () => {
    setAiBusy(true);
    setAiNotes("");
    try {
      const { data } = await api.post("/platform/blog/landing-seo");
      setSeo((s) => ({
        ...s,
        meta_title: data.meta_title || s.meta_title,
        meta_description: data.meta_description || s.meta_description,
        meta_keywords: data.meta_keywords || s.meta_keywords,
        og_title: data.og_title || s.og_title,
        focus_keyword: data.focus_keyword || s.focus_keyword,
        faq: Array.isArray(data.faq) && data.faq.length ? data.faq : (s.faq || []),
      }));
      const notes = Array.isArray(data.notes) ? data.notes.join("\n") : (data.notes || "");
      const ideas = (data.internal_link_ideas || []).map((x) => `${x.anchor} → ${x.path}`);
      setAiNotes([notes, ...ideas].filter(Boolean).join("\n"));
      toast.success("AI filled SEO fields — review and Save");
    } catch (e) { toast.error(extractErr(e)); } finally { setAiBusy(false); }
  };

  const faq = Array.isArray(seo?.faq) ? seo.faq : [];
  const setFaq = (next) => setSeo({ ...seo, faq: next });

  if (!seo || !mkt) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-8 max-w-4xl pb-24">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">SEO & Marketing</h1>
          <p className="text-slate-500 text-sm mt-1">Homepage metadata, FAQ schema, AI-assisted copy, analytics and pixels. Blog posts have their own AI SEO under Blog.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="seo-save-top" className="bg-indigo-500 hover:bg-indigo-600 shrink-0">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      {/* SEARCH APPEARANCE */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <Search className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Search &amp; social appearance</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <Field label="Meta title" hint={`Shown as the clickable headline on Google. ${(seo.meta_title || "").length}/60 characters.`}>
            <Input data-testid="seo-meta-title" className={inputCls} value={seo.meta_title || ""} onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })} placeholder="Mera Startup — India's Best Shopify Alternative" />
          </Field>
          <Field label="Meta description" hint={`Grey summary under the title on Google. ${(seo.meta_description || "").length}/160 characters.`}>
            <Textarea data-testid="seo-meta-description" className={inputCls} rows={3} value={seo.meta_description || ""} onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })} placeholder="Mera Startup helps Indian businesses launch a full ecommerce website…" />
          </Field>
          <Field label="OG title" hint="Optional. Social headline if different from meta title.">
            <Input data-testid="seo-og-title" className={inputCls} value={seo.og_title || ""} onChange={(e) => setSeo({ ...seo, og_title: e.target.value })} />
          </Field>
          <Field label="Focus keyword" hint="Primary query you want this homepage to rank for.">
            <Input data-testid="seo-focus-keyword" className={inputCls} value={seo.focus_keyword || ""} onChange={(e) => setSeo({ ...seo, focus_keyword: e.target.value })} placeholder="ecommerce website india" />
          </Field>
          <Field label="Keywords" hint="Comma-separated. Optional.">
            <Input data-testid="seo-keywords" className={inputCls} value={seo.meta_keywords || ""} onChange={(e) => setSeo({ ...seo, meta_keywords: e.target.value })} placeholder="ecommerce website india, online store builder, …" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Twitter / X handle" hint="e.g. @merastartup">
              <Input data-testid="seo-twitter" className={inputCls} value={seo.twitter_handle || ""} onChange={(e) => setSeo({ ...seo, twitter_handle: e.target.value })} placeholder="@merastartup" />
            </Field>
            <Field label="Social share image (OG image)" hint="1200×630 works best.">
              <div className="flex items-center gap-3">
                <div className="w-24 h-14 rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                  {seo.og_image ? <img src={mediaUrl(seo.og_image)} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-slate-500">None</span>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadOg(e.target.files[0])} data-testid="seo-og-file" />
                <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700" onClick={() => fileRef.current?.click()} data-testid="seo-og-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> {uploading ? "Uploading…" : "Upload"}
                </Button>
              </div>
            </Field>
          </div>
          <Input className={`${inputCls} font-mono-nums text-xs`} value={seo.og_image || ""} onChange={(e) => setSeo({ ...seo, og_image: e.target.value })} placeholder="OG image URL (or use Upload)" data-testid="seo-og-url" />
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Language" hint="html lang / hreflang">
              <Input className={inputCls} value={seo.default_language || ""} onChange={(e) => setSeo({ ...seo, default_language: e.target.value })} placeholder="en-IN" data-testid="seo-lang" />
            </Field>
            <Field label="Geo place" hint="geo.placename">
              <Input className={inputCls} value={seo.geo_placename || ""} onChange={(e) => setSeo({ ...seo, geo_placename: e.target.value })} placeholder="Mumbai" />
            </Field>
            <Field label="Geo region" hint="e.g. IN-MH">
              <Input className={inputCls} value={seo.geo_region || ""} onChange={(e) => setSeo({ ...seo, geo_region: e.target.value })} placeholder="IN-MH" />
            </Field>
          </div>
          <Field label="sameAs social URLs" hint="Company profiles only (LinkedIn company page, Instagram brand, YouTube, Crunchbase, Google Maps). Do not paste the founder’s personal profiles here — those already sit on Person schema.">
            <Input className={inputCls} value={seo.same_as || ""} onChange={(e) => setSeo({ ...seo, same_as: e.target.value })} placeholder="https://www.linkedin.com/company/merastartup, https://www.instagram.com/merastartup/" />
          </Field>
          <label className="flex items-center justify-between rounded-lg bg-slate-800 border border-slate-700 px-4 py-3">
            <span className="text-sm text-slate-200">Allow search engines to index this site
              <span className="block text-xs text-slate-500">Turn off to emit noindex,nofollow (hide from Google).</span>
            </span>
            <Switch checked={seo.robots_index !== false} onCheckedChange={(v) => setSeo({ ...seo, robots_index: v })} data-testid="seo-robots-index" />
          </label>
        </Card>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <Sparkles className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">AI SEO assistant</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-3">
          <p className="text-sm text-slate-400">Writes India-focused title, description, keywords and FAQ schema for the homepage via OpenRouter. Review, then Save. Add your OpenRouter key under Integrations.</p>
          <Button type="button" onClick={generateSeo} disabled={aiBusy || !claudeOn} data-testid="seo-claude-generate" className="bg-violet-600 hover:bg-violet-500">
            {aiBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {claudeOn ? "Generate homepage SEO" : "Add OpenRouter API key in Integrations"}
          </Button>
          {aiNotes ? <pre className="text-xs text-slate-400 whitespace-pre-wrap bg-slate-950 border border-slate-800 rounded-lg p-3">{aiNotes}</pre> : null}
        </Card>
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <HelpCircle className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Homepage FAQ (schema + landing page)</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <p className="text-xs text-slate-500">These become FAQPage structured data and a visible FAQ block on the marketing site — both help featured snippets.</p>
          {faq.map((item, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-2">
              <Input className={inputCls} placeholder="Question" value={item.q || ""} onChange={(e) => setFaq(faq.map((x, j) => j === i ? { ...x, q: e.target.value } : x))} />
              <Textarea className={inputCls} rows={2} placeholder="Answer" value={item.a || ""} onChange={(e) => setFaq(faq.map((x, j) => j === i ? { ...x, a: e.target.value } : x))} />
            </div>
          ))}
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200" onClick={() => setFaq([...faq, { q: "", a: "" }])}>Add FAQ</Button>
            {faq.length > 0 && <Button type="button" variant="outline" className="border-slate-700 bg-slate-800 text-slate-200" onClick={() => setFaq(faq.slice(0, -1))}>Remove last</Button>}
          </div>
        </Card>
      </section>

      {/* SEARCH ENGINE VERIFICATION */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <Globe className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Search-engine verification</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <Field label="Google Search Console verification code" hint="Paste only the token (the content= value). Do not paste google-site-verification=.">
            <Input data-testid="seo-gsc" className={`${inputCls} font-mono-nums text-xs`} value={seo.google_site_verification || ""} onChange={(e) => setSeo({ ...seo, google_site_verification: e.target.value.replace(/^google-site-verification\s*[=:]\s*/i, "").trim() })} placeholder="e.g. abcDEF123..." />
          </Field>
          <Field label="Bing Webmaster verification code" hint="The content value of the msvalidate.01 meta tag.">
            <Input data-testid="seo-bing" className={`${inputCls} font-mono-nums text-xs`} value={seo.bing_site_verification || ""} onChange={(e) => setSeo({ ...seo, bing_site_verification: e.target.value })} placeholder="e.g. 0123456789ABCDEF" />
          </Field>
        </Card>
      </section>

      {/* ANALYTICS */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <BarChart3 className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Analytics</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <Field label="Google Analytics 4 Measurement ID" hint="Starts with G- (e.g. G-XXXXXXXXXX).">
            <Input data-testid="seo-ga4" className={`${inputCls} font-mono-nums`} value={seo.ga4_id || ""} onChange={(e) => setSeo({ ...seo, ga4_id: e.target.value })} placeholder="G-XXXXXXXXXX" />
          </Field>
          <Field label="Google Tag Manager Container ID" hint="Starts with GTM- (e.g. GTM-XXXXXX). Optional.">
            <Input data-testid="seo-gtm" className={`${inputCls} font-mono-nums`} value={seo.gtm_id || ""} onChange={(e) => setSeo({ ...seo, gtm_id: e.target.value })} placeholder="GTM-XXXXXX" />
          </Field>
        </Card>
      </section>

      {/* ADS / PIXELS */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 text-indigo-300">
          <Megaphone className="w-5 h-5" />
          <h2 className="font-display text-lg font-bold text-slate-100">Advertising pixels</h2>
        </div>
        <Card className="bg-slate-900 border-slate-800 p-5 space-y-4">
          <p className="text-xs text-slate-500">Add these when you plan to run traffic ads. They fire a PageView on the landing page so your campaigns can optimise and track conversions.</p>
          <Field label="Facebook / Meta Pixel ID" hint="15–16 digit number from Meta Events Manager.">
            <Input data-testid="mkt-fb-pixel" className={`${inputCls} font-mono-nums`} value={mkt.fb_pixel_id || ""} onChange={(e) => setMkt({ ...mkt, fb_pixel_id: e.target.value })} placeholder="1234567890123456" />
          </Field>
          <Field label="Google Ads ID (gtag)" hint="Starts with AW- (e.g. AW-XXXXXXXXX).">
            <Input data-testid="mkt-google-ads" className={`${inputCls} font-mono-nums`} value={mkt.google_ads_id || ""} onChange={(e) => setMkt({ ...mkt, google_ads_id: e.target.value })} placeholder="AW-XXXXXXXXX" />
          </Field>
        </Card>
      </section>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="seo-save-bottom" className="bg-indigo-500 hover:bg-indigo-600 shadow-lg">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
